import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LinearProgress, Stack, Box } from '@mui/material';

import { useSession, useWorkspaceTabs } from 'store';
import { NavBar, TopBar, WorkspaceTabs } from './components';
import navCfg from './components/NavBar/cfg';
import {
  getWorkspaceTabRoutes,
  getWorkspaceTabUrl,
  isWorkspaceUrlForTab,
  resolveWorkspaceTab,
  toWorkspaceUrl,
} from './workspaceTabRoutes';

const Main = (_props) => {
  const [openNavBarDrawer, setOpenNavBarDrawer] = useState(false);
  const [navBrief, setNavBrief] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const role = useSession(state => state.session?.role);
  const openedKeys = useWorkspaceTabs(state => state.openedKeys);
  const lastUrls = useWorkspaceTabs(state => state.lastUrls);
  const closeTab = useWorkspaceTabs(state => state.closeTab);
  const reconcileLocation = useWorkspaceTabs(state => state.reconcileLocation);
  const sanitizeTabs = useWorkspaceTabs(state => state.sanitizeTabs);

  const tabRoutes = useMemo(
    () => getWorkspaceTabRoutes(navCfg, role === 'admin'),
    [role]
  );
  const routeByKey = useMemo(
    () => new Map(tabRoutes.map(route => [route.key, route])),
    [tabRoutes]
  );
  const activeRoute = useMemo(
    () => resolveWorkspaceTab(location.pathname, tabRoutes),
    [location.pathname, tabRoutes]
  );
  const currentUrl = toWorkspaceUrl(location);

  useEffect(() => {
    sanitizeTabs(
      tabRoutes.map(route => route.key),
      (key, url) => isWorkspaceUrlForTab(key, url, tabRoutes)
    );
  }, [sanitizeTabs, tabRoutes]);

  useEffect(() => {
    if (activeRoute) {
      reconcileLocation(activeRoute.key, currentUrl);
    }
  }, [activeRoute, currentUrl, reconcileLocation]);

  const tabs = useMemo(
    () => openedKeys.map(key => routeByKey.get(key)).filter(Boolean),
    [openedKeys, routeByKey]
  );
  const activeKey = activeRoute?.key || false;

  const selectTab = useCallback((key) => {
    navigate(getWorkspaceTabUrl(key, lastUrls[key], tabRoutes));
  }, [lastUrls, navigate, tabRoutes]);

  const closeWorkspaceTab = useCallback((key) => {
    const closingActive = activeRoute?.key === key;
    const nextKey = closeTab(key);
    if (!nextKey) {
      return null;
    }
    if (closingActive) {
      navigate(getWorkspaceTabUrl(nextKey, lastUrls[nextKey], tabRoutes), {replace: true});
    }
    return closingActive ? nextKey : activeRoute?.key || null;
  }, [activeRoute, closeTab, lastUrls, navigate, tabRoutes]);

  return (
    <Stack sx={{
      height: '100vh',
      width: '100%',
      overflow: 'hidden'
    }}>
      <TopBar
        styles={{zIndex: 2, position: 'relative'}}
        hideNavBar='auto'
        onOpenNavBarDrawer={() => setOpenNavBarDrawer(true)}
      >
        <WorkspaceTabs
          activeKey={activeKey}
          appBar
          onClose={closeWorkspaceTab}
          onSelect={selectTab}
          tabs={tabs}
        />
      </TopBar>
      <Stack direction="row" sx={{ flex: '1 1 auto', overflow: 'hidden'}}>
        <NavBar
          styles={{ zIndex: 3, flex: '0 0 auto' }}
          onDrawerClose={() => setOpenNavBarDrawer(false)}
          openDrawer={openNavBarDrawer}
          hide='auto'
          brief={navBrief}
          onBriefToggle={() => setNavBrief(brief => !brief)}
        />
        <Stack sx={{flex: '1 1 auto', minWidth: 0, overflow: 'hidden'}}>
          <Box sx={{overflowY: 'auto', flex: '1 1 auto', minHeight: 0}}>
            <Suspense fallback={<LinearProgress />}>
              {/* Keying the outlet by workspace tab preserves URL-level tabs:
                  switching tabs remounts the page without keeping it alive. */}
              <Outlet key={activeKey || `unmatched:${location.pathname}`} />
            </Suspense>
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Main;
